import React from 'react'
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ReferenceArea, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { dayMonth, formatNumber, monthFullName, monthVariationText } from '../../../utilities/dashboard'
import ChartTooltip, { BarLabel } from './ChartTooltip'
import { axisTick, CHART_DESC } from './DailyChart'
import useChartColors from './useChartColors'

const FADED_OPACITY = 0.35 // meses antes de jul/2024: só a importação

// Legenda do mês a mês (a do ano anterior some quando ele não tem dado)
export const MonthlyLegend = ({ rows, year, previousYear }) => {
  const { chart: colors } = useChartColors()
  const hasPrevious = rows.some((row) => row.previousShown > 0)
  const hasPartial = rows.some((row) => row.partial && row.previousRest)
  return (
    <span className='dashboard-legend' aria-hidden='true'>
      {hasPrevious ? <span><i className='dashboard-swatch' style={{ background: colors.previous }} />{previousYear}</span> : ''}
      <span><i className='dashboard-swatch' style={{ background: colors.primary }} />{year}</span>
      {hasPartial ? <span><i className='dashboard-swatch dashboard-swatch-dashed' style={{ borderColor: colors.previous }} />{previousYear}, mês inteiro</span> : ''}
    </span>
  )
}

// Ano base × ano anterior. Mês parcial: a barra do ano anterior mostra os mesmos dias (até ontem) e o resto do mês vem
// empilhado como contorno tracejado; a do ano base leva o rótulo "parcial". Sem transparência no parcial: o roxo forte
// esmaecido ficaria igual ao claro do ano anterior
const MonthlyComparisonChart = ({ rows, year, previousYear, status, selected, narrow, title }) => {
  const { chart: colors } = useChartColors()
  // No celular, "data futura" (mês seguinte ao parcial) encostaria em "parcial": fica no contorno, no tooltip e na tabela
  const data = narrow ? rows.map((row) => ({ ...row, partialTag: row.partial ? 'parcial' : null })) : rows

  const tooltipFor = (row) => {
    // Mês parcial sem nenhum dia completo (dia 1; em 01/01 o compared_until vem null): ainda não há o que comparar
    const until = row.comparedUntil ? dayMonth(row.comparedUntil) : ''
    const partialText = until ? `compara até ${until}` : 'ainda sem dia completo'
    const tooltip = { title: monthFullName(row.month) + (row.partial ? ` (parcial: ${partialText})` : ''), rows: [], notes: [] }

    if (row.future) {
      tooltip.rows.push({ color: colors.primary, dashed: true, value: formatNumber(row.base), label: `${year}, com data futura` })
      tooltip.notes.push('Atendimentos lançados com data futura. Corrija a data no prontuário.')
    } else if (row.partial) {
      // A barra é o total até agora (como os cards); ao lado, o número que entra na variação, até ontem como o ano anterior
      const sameDays = until ? ` · ${formatNumber(row.currentSameDays)} até ${until}` : ''
      tooltip.rows.push({ color: colors.primary, value: formatNumber(row.base), label: `${year}, até agora${sameDays}` })
    } else {
      tooltip.rows.push({ color: colors.primary, value: formatNumber(row.base), label: String(year) })
    }

    if (row.partial) {
      if (until) tooltip.rows.push({ color: colors.previous, value: formatNumber(row.previousShown), label: `${previousYear}, até ${until}` })
      tooltip.notes.push(`${previousYear}, mês inteiro (contorno): ${formatNumber(row.previousTotal)}`)
    } else {
      tooltip.rows.push({ color: colors.previous, value: formatNumber(row.previousShown), label: String(previousYear) })
    }

    const variation = monthVariationText(row, status)
    if (variation !== '—') tooltip.notes.push(`variação: ${variation}`)
    if (row.base === null && !row.future) tooltip.notes.push('mês ainda sem dados')
    if (row.previousFaded) tooltip.notes.push(`${row.label}/${previousYear}: só a importação, sem comparação`)
    if (row.baseFaded) tooltip.notes.push(`${row.label}/${year}: também só a importação`)
    return tooltip
  }

  return (
    <ResponsiveContainer width='100%' height={260}>
      <BarChart data={data} margin={{ top: 22, right: 8, bottom: 0, left: -10 }} barGap={2} barCategoryGap='24%' accessibilityLayer title={title} desc={CHART_DESC}>
        {selected ? <ReferenceArea x1={selected.from} x2={selected.to} fill={colors.shade} fillOpacity={1} /> : ''}
        <CartesianGrid vertical={false} stroke={colors.grid} />
        <XAxis dataKey='label' interval={0} tickLine={false} axisLine={{ stroke: colors.axis }} tick={axisTick(colors)} />
        <YAxis allowDecimals={false} width={44} tickLine={false} axisLine={false} tick={axisTick(colors)} />
        <Tooltip cursor={{ fill: colors.cursor }} content={<ChartTooltip render={tooltipFor} />} isAnimationActive={false} />

        <Bar dataKey='previousShown' stackId='previous' name={String(previousYear)} fill={colors.previous} maxBarSize={24} isAnimationActive={false}>
          {rows.map((row) => (
            <Cell key={row.month} fillOpacity={row.previousFaded ? FADED_OPACITY : 1} radius={row.previousRest ? [0, 0, 0, 0] : [4, 4, 0, 0]} />
          ))}
        </Bar>
        <Bar
          dataKey='previousRest'
          stackId='previous'
          name={`${previousYear}, mês inteiro`}
          fill='none'
          stroke={colors.previous}
          strokeWidth={1.5}
          strokeDasharray='4 3'
          radius={[4, 4, 0, 0]}
          maxBarSize={24}
          isAnimationActive={false}
        />
        <Bar dataKey='base' name={String(year)} fill={colors.primary} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false}>
          {rows.map((row) => (
            <Cell
              key={row.month}
              fill={row.future ? 'none' : colors.primary}
              stroke={row.future ? colors.primary : 'none'}
              strokeWidth={row.future ? 1.5 : 0}
              strokeDasharray={row.future ? '4 3' : undefined}
              fillOpacity={row.baseFaded ? FADED_OPACITY : 1}
            />
          ))}
          <LabelList dataKey='partialTag' content={<BarLabel fontSize={10} fontWeight={600} />} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

export default MonthlyComparisonChart
